import EVImg from '@components/EVImg/EVImg';
import AspectBox from '@components/Layout/AspectBox';
import ConditionallyWrapWithUrl from '@components/MiscComponents/ConditionallyWrapWithUrl';
import {
  CarouselNavButtonLeft,
  CarouselNavButtonRight,
} from '@components/MiscComponents/GridAndCarouselComponents';
import Video from '@components/Video';
import { FC } from 'react';
import { A11y, Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  ImageDetailHeader,
  ImageDetails,
  ImageDetailSubHeader,
  ImageLinerGradientOverlay,
  ImageSection,
} from './styles-imagesection';

export interface CarouselImage {
  key: string;
  header: string;
  subHeader: string;
  imgFocalPoint?: any;
  ctaUrl?: string;
  source?: string;
  videoUpload?: string;
  videoSrc?: string;
}

interface SearchHeroCarouselProps {
  propertyImages: CarouselImage[];
}

const SearchHeroCarousel: FC<SearchHeroCarouselProps> = ({ propertyImages }) => {
  let imgData: any = propertyImages[0];

  const { source, videoUpload, videoSrc, imgFocalPoint, ctaUrl, header, subHeader } =
    propertyImages[0]!;

  if (imgData.imgFocalPoint != null) imgData = imgData.imgFocalPoint;
  // a separate return if only one property is passed in to prevent rendering a carousel if possible
  if (propertyImages.length === 1) {
    if (source && source === 'Video Upload' && videoUpload) {
      return (
        <AspectBox $ratio={16 / 9}>
          <Video
            video_source={false}
            videoUpload={videoUpload}
            posterImage={imgFocalPoint ? imgFocalPoint.fields?.img?.file?.url : null}
            muted={true}
            loop={true}
            controls={false}
          />
        </AspectBox>
      );
    } else if (source && source === 'Video Url' && videoSrc) {
      return (
        <AspectBox $ratio={16 / 9}>
          <Video
            video_source={true}
            youTubeUrl={videoSrc}
            posterImage={imgFocalPoint ? imgFocalPoint.fields?.img?.file?.url : null}
            muted={true}
            loop={true}
          />
        </AspectBox>
      );
    } else {
      // Default values for source aren't set for all content types
      // Image source will either be equal to 'Image Upload' or null
      return (
        <ImageSection key={'1'}>
          <AspectBox $ratio={[1, 2 / 1, 2 / 1]}>
            <ImageDetails>
              <ConditionallyWrapWithUrl href={ctaUrl}>
                <ImageDetailHeader>{header}</ImageDetailHeader>
                <ImageDetailSubHeader>{subHeader}</ImageDetailSubHeader>
              </ConditionallyWrapWithUrl>
            </ImageDetails>
            <EVImg imageFocalPointData={imgData} dataTestId="search-hero-image" />
          </AspectBox>
        </ImageSection>
      );
    }
  } else {
    return (
      <ImageSection>
        <AspectBox $ratio={[1, 2 / 1, 2 / 1]}>
          <Swiper
            modules={[Navigation, Pagination, A11y, EffectFade, Autoplay]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            autoplay={{ delay: 5000 }}
            spaceBetween={0}
            slidesPerView={1}
            pagination={{
              clickable: true,
            }}
            navigation={{
              prevEl: '.searchhero-button-prev',
              nextEl: '.searchhero-button-next',
            }}
            loop={true}
          >
            {propertyImages.map((image, imgInx) => {
              let imgData: any = image;
              if (imgData.imgFocalPoint != null) imgData = imgData.imgFocalPoint;
              return (
                <SwiperSlide key={imgInx}>
                  {image.header && image.subHeader && (
                    <ImageDetails>
                      <ConditionallyWrapWithUrl href={image.ctaUrl}>
                        {image.header && <ImageDetailHeader>{image.header}</ImageDetailHeader>}
                        {image.subHeader && (
                          <ImageDetailSubHeader>{image.subHeader}</ImageDetailSubHeader>
                        )}
                      </ConditionallyWrapWithUrl>
                    </ImageDetails>
                  )}
                  <ImageLinerGradientOverlay />
                  <EVImg imageFocalPointData={imgData} dataTestId={`search-hero-image-${imgInx}`} />
                </SwiperSlide>
              );
            })}
            <CarouselNavButtonLeft
              variant="dark"
              className="swiper-button-prev searchhero-button-prev"
            />
            <CarouselNavButtonRight
              variant="dark"
              className="swiper-button-next searchhero-button-next"
            />
          </Swiper>
        </AspectBox>
      </ImageSection>
    );
  }
};

export default SearchHeroCarousel;
